import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { api, API } from "../lib/api";

/**
 * Image uploader for admin forms.
 * Props:
 *   value: array of URLs (string[])
 *   onChange: (newUrls) => void
 *   max: max number of images (default 6)
 */
export const ImageUpload = ({ value = [], onChange, max = 6, testIdPrefix = "image-upload", recommendedSize = "" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const resolveUrl = (u) => {
    if (!u) return u;
    if (u.startsWith("http")) return u;
    if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
    return u;
  };

  const handleFiles = async (files) => {
    setError("");
    const remaining = max - value.length;
    const list = Array.from(files).slice(0, remaining);
    if (list.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      onChange([...value, ...uploaded]);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  const addUrl = () => {
    const url = window.prompt("Paste an image URL");
    if (url && url.trim()) onChange([...value, url.trim()]);
  };

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="flex flex-wrap gap-3">
        {value.map((u, i) => (
          <div key={i} className="relative w-24 h-24 border border-kult-border" data-testid={`${testIdPrefix}-thumb-${i}`}>
            <img src={resolveUrl(u)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute -top-2 -right-2 bg-kult-pink text-black p-1"
              aria-label="Remove"
              data-testid={`${testIdPrefix}-remove-${i}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {value.length < max && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="w-24 h-24 border-2 border-dashed border-kult-border hover:border-kult-pink flex flex-col items-center justify-center text-kult-muted hover:text-kult-pink disabled:opacity-50"
              data-testid={`${testIdPrefix}-button`}
            >
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
              <span className="font-mono text-[9px] tracking-[0.2em] mt-1">UPLOAD</span>
            </button>
            <button
              type="button"
              onClick={addUrl}
              className="w-24 h-24 border border-kult-border hover:border-kult-pink flex flex-col items-center justify-center text-kult-muted hover:text-kult-pink"
              data-testid={`${testIdPrefix}-url-button`}
            >
              <span className="font-mono text-[9px] tracking-[0.2em]">PASTE URL</span>
            </button>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={max > 1}
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
      />
      {error && <p className="font-mono text-xs text-kult-pink">{error}</p>}
      <p className="font-mono text-[10px] tracking-[0.2em] text-kult-muted">
        JPG / PNG / WEBP up to 5MB · {value.length}/{max}
        {recommendedSize && (
          <span className="block text-kult-yellow mt-1" data-testid={`${testIdPrefix}-recommended`}>
            ⚡ Recommended: {recommendedSize}
          </span>
        )}
      </p>
    </div>
  );
};
